import { AI_INFERENCE_ENDPOINTS } from './config';
import { apiFetch } from './client';
import { formatApiValidationDetail } from './apiErrors';

/**
 * POST /api/v1/ai-inference
 * Generic AI inference task runner.
 * For scene understanding (generate_mask) and material_transfer, etc.
 * @param {{ task_name?: string, room_image_id: number, [key: string]: any }} payload
 * @returns {Promise<any>}
 */
export async function runAiInference(payload) {
  const body = {
    ...payload,
    task_name: payload.task_name ?? 'generate_mask',
  };

  const res = await apiFetch(AI_INFERENCE_ENDPOINTS.run, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  const result = await res.json().catch(() => ({}));

  if (!res.ok) {
    const detailMsg = formatApiValidationDetail(result.detail);
    const msg =
      res.status === 503
        ? 'Service is not available, please try again later.'
        : detailMsg || result.message || result.error || `AI inference failed (${res.status})`;
    const err = new Error(msg);
    err.status = res.status;
    err.taskId = result?.taskId ?? result?.aiTaskId ?? result?.id ?? null;
    err.details = result;
    throw err;
  }

  return result;
}

/** Format AI inference task payload for display in the Prompt chat. */
export function formatAiInferenceResponseForDisplay(data) {
  if (data == null) return 'No response received.';

  const status = data.status ?? data.taskStatus ?? data.state;
  const errMsg = data.message ?? data.error;
  const normalizedStatus = status != null ? String(status).toLowerCase() : '';

  if (normalizedStatus === 'failed' || normalizedStatus === 'error') {
    return errMsg ? `Failed: ${errMsg}` : 'Task failed.';
  }

  const lines = [];
  if (status) lines.push(`Status: ${status}`);
  if (errMsg) lines.push(String(errMsg));

  const taskResult = data.taskResult ?? data.result;
  if (taskResult != null) {
    if (typeof taskResult === 'string') {
      lines.push(taskResult);
    } else {
      try {
        lines.push(JSON.stringify(taskResult, null, 2));
      } catch {
        lines.push(String(taskResult));
      }
    }
  } else if (lines.length === 0) {
    try {
      return JSON.stringify(data, null, 2);
    } catch {
      return String(data);
    }
  }

  return lines.join('\n\n');
}

/**
 * POST /api/v1/ai-inference (multipart/form-data)
 * Runs AI inference with an uploaded image file (no room image upload required).
 * @param {{ task_name?: string, [key: string]: any }} payload
 * @param {File|Blob} imageFile
 * @param {string} imageFieldName
 * @returns {Promise<any>}
 */
export async function runAiInferenceWithImage(payload, imageFile, imageFieldName = 'image') {
  const formData = new FormData();
  const body = {
    ...payload,
    task_name: payload?.task_name ?? 'generate_mask',
  };

  Object.entries(body).forEach(([key, value]) => {
    if (value == null) return;
    if (Array.isArray(value)) {
      formData.append(key, JSON.stringify(value));
      return;
    }
    if (typeof value === 'object') {
      formData.append(key, JSON.stringify(value));
      return;
    }
    formData.append(key, String(value));
  });
  formData.append(imageFieldName, imageFile);

  const res = await apiFetch(AI_INFERENCE_ENDPOINTS.run, {
    method: 'POST',
    body: formData,
  });

  const text = await res.text().catch(() => '');
  let result = {};
  try {
    result = text ? JSON.parse(text) : {};
  } catch {
    result = text ? { message: text } : {};
  }

  if (!res.ok) {
    const msg =
      res.status === 503
        ? 'Service is not available, please try again later.'
        : result.message || result.error || `AI inference failed (${res.status})`;
    const err = new Error(msg);
    err.status = res.status;
    err.taskId = result?.taskId ?? result?.aiTaskId ?? result?.id ?? null;
    err.details = result;
    throw err;
  }

  return result;
}

/**
 * GET /api/v1/ai-inference/{taskId}
 * Fetches task status and result (same shape as WebSocket completion when done).
 * @param {number|string} taskId
 * @returns {Promise<any>}
 */
export async function getAiInferenceResult(taskId) {
  const res = await apiFetch(AI_INFERENCE_ENDPOINTS.getResult(taskId));

  const result = await res.json().catch(() => ({}));

  if (!res.ok) {
    const msg = result.message || result.error || `Failed to get inference result (${res.status})`;
    const err = new Error(msg);
    err.status = res.status;
    throw err;
  }

  console.log(`GET /ai-inference/${taskId} response:`, result);
  return result;
}

/**
 * POST /api/v1/ai-inference/{task_id}/cancel
 * Cancel a PENDING or STARTED AI task and revoke its Celery job.
 * @param {number|string} taskId
 * @returns {Promise<any>}
 */
export async function cancelAiInferenceTask(taskId) {
  const res = await apiFetch(AI_INFERENCE_ENDPOINTS.cancel(taskId), {
    method: 'POST',
  });

  const result = await res.json().catch(() => ({}));

  if (!res.ok) {
    const detailMsg = formatApiValidationDetail(result.detail);
    const msg =
      detailMsg || result.message || result.error || `Failed to cancel task (${res.status})`;
    const err = new Error(msg);
    err.status = res.status;
    err.details = result;
    throw err;
  }

  console.log(`POST /ai-inference/${taskId}/cancel response:`, result);
  return result;
}

/**
 * GET /api/v1/ai-inference/presigned-url?object-name={result_s3_key}
 * Returns a presigned URL for the AI result image.
 * @param {string} objectName
 * @returns {Promise<{ url?: string, presignedUrl?: string, [key: string]: any }>}
 */
export async function getAiInferencePresignedUrl(objectName) {
  const path = AI_INFERENCE_ENDPOINTS.getPresignedUrl(objectName);
  const res = await apiFetch(path);
  const text = await res.text().catch(() => '');
  if (!res.ok) {
    let parsed;
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = null;
    }
    const msg = (parsed && (parsed.message || parsed.error)) || text || `Failed to get presigned URL (${res.status})`;
    throw new Error(msg);
  }

  // Backend currently returns the URL as a plain string (or JSON string), not an object.
  // Try to parse JSON first; if that yields a string, wrap it; if parsing fails, treat the body as the URL string.
  let result;
  try {
    const parsed = JSON.parse(text);
    result = typeof parsed === 'string' ? { url: parsed } : parsed;
  } catch {
    result = { url: text };
  }
  console.log('GET /ai-inference/presigned-url response:', result);
  return result;
}
